/**
 * get-ab-context.ts — A/B experiment context for signup logging.
 *
 * Surfaces the landing-page visitor id (AdsLandingVisitorId cookie) and the
 * new-landing-page experiment assignment (NewLandingPageExperiment cookie, e.g.
 * "treatment1" / "treatment2") so signup IdP-selection clicks can be sliced by
 * treatment cohort in Kusto. Both values are read from cookies set by the new
 * landing page earlier in the journey.
 */

const EXPERIMENT_COOKIE = 'NewLandingPageExperiment';
const VISITOR_COOKIE = 'AdsLandingVisitorId';

export type AbContext = {
  /** Landing-page visitor id (AdsLandingVisitorId cookie); empty string when not present. */
  visitorId: string;
  /** New-landing-page experiment assignment; 'none' when not present. */
  experiment: string;
};

/**
 * Read a single cookie value by name from document.cookie.
 * Returns an empty string when the cookie is absent or document is unavailable.
 * @param {string} name - The cookie name to read.
 * @returns {string} The decoded cookie value, or '' when not present.
 */
export const readCookie = (name: string): string => {
  if (typeof document === 'undefined' || !document.cookie) {
    return '';
  }
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  if (!match) {
    return '';
  }

  const rawValue = match[1];
  try {
    return decodeURIComponent(rawValue);
  } catch {
    // Malformed percent-encoding can throw URIError; don't block signup navigation.
    return rawValue;
  }
};

/**
 * Resolve the A/B context (visitor id + experiment assignment) for signup logging.
 * @returns {AbContext} The visitor id and experiment assignment.
 */
export const getAbContext = (): AbContext => ({
  visitorId: readCookie(VISITOR_COOKIE),
  experiment: readCookie(EXPERIMENT_COOKIE) || 'none',
});
